import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";
import Select from "react-select";
import Swal from "sweetalert2";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import apiurl from "../Api";
import "./css/advancePayment.css";
import "./css/mails.css";

const tabs = [
  { key: "payroll", label: "Payroll", icon: "ri-secure-payment-line" },
  { key: "others", label: "Others", icon: "ri-file-list-3-line" },
  { key: "sent", label: "Sent", icon: "ri-send-plane-line" },
];

const progressOptions = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

const sectionToggleOptionsByTab = {
  payroll: [
    { value: "", label: "All" },
    { value: "invoice", label: "Invoice Generation" },
    { value: "payment", label: "Payments" },
    { value: "uncoded", label: "Payroll Uncoded" },
  ],
  others: [
    { value: "", label: "All" },
    { value: "invoice", label: "Invoice Generation" },
    { value: "payment", label: "Payments" },
  ],
};

const progressFilterOptions = [{ value: "", label: "All Progress" }, ...progressOptions];

const dateRangeOptions = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "custom", label: "Custom Range" },
];

function toDateInputValue(date) {
  return date.toISOString().slice(0, 10);
}

function computeDateRange(preset, customFrom, customTo) {
  const today = new Date();
  if (preset === "today") {
    const d = toDateInputValue(today);
    return { from: d, to: d };
  }
  if (preset === "7d") {
    const start = new Date(today);
    start.setDate(start.getDate() - 6);
    return { from: toDateInputValue(start), to: toDateInputValue(today) };
  }
  if (preset === "30d") {
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return { from: toDateInputValue(start), to: toDateInputValue(today) };
  }
  if (preset === "custom") {
    return { from: customFrom, to: customTo };
  }
  return { from: "", to: "" };
}

function fileIcon(name, className) {
  const ext = (name || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return <PictureAsPdfRoundedIcon className={`${className} text-danger`} />;
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
    return <ImageRoundedIcon className={`${className} text-primary`} />;
  }
  if (["xls", "xlsx", "csv"].includes(ext)) {
    return <TableChartRoundedIcon className={`${className} text-success`} />;
  }
  if (["doc", "docx"].includes(ext)) {
    return <DescriptionRoundedIcon className={`${className} text-info`} />;
  }
  return <InsertDriveFileRoundedIcon className={`${className} text-secondary`} />;
}

function AttachmentTile({ name, label, onView, onDownload }) {
  return (
    <div className="mails-attachment-tile" onClick={onView} title={name}>
      {onDownload && (
        <button
          type="button"
          className="mails-attachment-tile-download"
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
          title="Download"
        >
          <i className="ri-download-2-line" />
        </button>
      )}
      {fileIcon(name, "mails-attachment-tile-icon")}
      <span className="mails-attachment-tile-name">
        {name}
        {label ? <span className="text-muted"> {label}</span> : null}
      </span>
    </div>
  );
}

function Mails() {
  const [activeTab, setActiveTab] = useState("payroll");
  const [sectionFilter, setSectionFilter] = useState("");
  const [progressFilter, setProgressFilter] = useState(progressFilterOptions[0]);
  const [assignedFilter, setAssignedFilter] = useState({ value: "", label: "Everyone" });
  const [dateRangePreset, setDateRangePreset] = useState(dateRangeOptions[0]);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;
  const [sentRows, setSentRows] = useState([]);
  const [sentTotal, setSentTotal] = useState(0);
  const [sentPage, setSentPage] = useState(1);
  const [sentSearch, setSentSearch] = useState("");
  const [sentLoading, setSentLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState("");
  const [currentUserPost, setCurrentUserPost] = useState("");
  const [accountsStaff, setAccountsStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewMail, setPreviewMail] = useState(null);
  const [assigningId, setAssigningId] = useState(null);

  const isAccountsHead = currentUserPost === "accounts_head";
  const [sheetPreview, setSheetPreview] = useState(null);
  const [sheetActiveTab, setSheetActiveTab] = useState(0);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState("");
  const [detailMailId, setDetailMailId] = useState(null);
  const [detailMail, setDetailMail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [attachmentsListRow, setAttachmentsListRow] = useState(null);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [replyLoadingId, setReplyLoadingId] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);
  const [showQuoted, setShowQuoted] = useState(true);

  const isInlinePreviewable = (name) => /\.(pdf|jpg|jpeg|png)$/i.test(name || "");
  const isSpreadsheetPreviewable = (name) => /\.(xlsx?|csv)$/i.test(name || "");

  const buildListParams = useCallback(() => {
    const { from, to } = computeDateRange(dateRangePreset?.value, customFrom, customTo);
    const params = { mode: "list", type: activeTab, page, page_size: pageSize };
    if (sectionFilter) params.section = sectionFilter;
    if (progressFilter?.value) params.progress = progressFilter.value;
    if (assignedFilter?.value) params.assigned = assignedFilter.value;
    if (from) params.from = from;
    if (to) params.to = to;
    if (search.trim()) params.q = search.trim();
    return params;
  }, [activeTab, sectionFilter, progressFilter, assignedFilter, dateRangePreset, customFrom, customTo, search, page]);

  const fetchMails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiurl}mails.php`, { params: buildListParams() });
      if (response.data?.status === true) {
        setRows(response.data.data || []);
        setTotal(response.data.total || 0);
        setCurrentUser(response.data.current_user || "");
        setCurrentUserPost(response.data.current_user_post || "");
        setAccountsStaff(response.data.accounts_staff || []);
      } else {
        setRows([]);
        setTotal(0);
      }
    } catch (error) {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildListParams]);

  const refreshSilently = useCallback(async () => {
    try {
      const response = await axios.get(`${apiurl}mails.php`, { params: buildListParams() });
      if (response.data?.status === true) {
        setRows(response.data.data || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      // silent — next poll will retry
    }
  }, [buildListParams]);

  const [syncing, setSyncing] = useState(false);

  const manualSync = async () => {
    try {
      setSyncing(true);
      const syncResponse = await axios.get(`${apiurl}mails.php`, { params: { mode: "sync" } });
      await fetchMails();
      if (syncResponse.data?.status === true) {
        const processed = syncResponse.data.processed || 0;
        Swal.fire({
          icon: "success",
          title: "Synced",
          text: processed > 0 ? `${processed} new mail(s) fetched.` : "No new mail — you're up to date.",
          timer: 1800,
          showConfirmButton: false,
        });
      } else {
        Swal.fire("Sync failed", syncResponse.data?.message || "Could not sync mails", "error");
      }
    } catch (error) {
      Swal.fire("Sync failed", "Could not reach the mail server", "error");
    } finally {
      setSyncing(false);
    }
  };

  const fetchSentMails = useCallback(async () => {
    try {
      setSentLoading(true);
      const params = { mode: "list_sent", page: sentPage, page_size: pageSize };
      if (sentSearch.trim()) params.q = sentSearch.trim();
      const response = await axios.get(`${apiurl}mails.php`, { params });
      if (response.data?.status === true) {
        setSentRows(response.data.data || []);
        setSentTotal(response.data.total || 0);
      } else {
        setSentRows([]);
        setSentTotal(0);
      }
    } catch (error) {
      setSentRows([]);
      setSentTotal(0);
    } finally {
      setSentLoading(false);
    }
  }, [sentPage, sentSearch]);

  useEffect(() => {
    if (activeTab === "sent") {
      const timer = setTimeout(fetchSentMails, sentSearch ? 350 : 0);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(fetchMails, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [activeTab, fetchMails, fetchSentMails, search, sentSearch]);

  useEffect(() => {
    axios
      .get(`${apiurl}mails.php`, { params: { mode: "sync" } })
      .finally(fetchMails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(refreshSilently, 20000);
    return () => clearInterval(interval);
  }, [refreshSilently]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSectionFilter("");
    setSearch("");
    setPage(1);
  };

  const clearFilters = () => {
    setSectionFilter("");
    setProgressFilter(progressFilterOptions[0]);
    setAssignedFilter({ value: "", label: "Everyone" });
    setDateRangePreset(dateRangeOptions[0]);
    setCustomFrom("");
    setCustomTo("");
    setSearch("");
    setPage(1);
  };

  const assignedFilterOptions = [
    { value: "", label: "Everyone" },
    { value: "me", label: "Assigned to Me" },
    { value: "unassigned", label: "Unassigned" },
    ...(isAccountsHead ? accountsStaff.map((s) => ({ value: s.username, label: s.name })) : []),
  ];

  const typeBadgeClass = (type) => {
    if (type === "payroll") return "bg-info-transparent text-info";
    return "bg-secondary-transparent text-secondary";
  };

  const formatDateTime = (value) => {
    if (!value) return "";
    const d = new Date(value.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDatePart = (value) => {
    if (!value) return "";
    const d = new Date(value.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const formatTimePart = (value) => {
    if (!value) return "";
    const d = new Date(value.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };

  const progressColorClass = (progress) => {
    if (progress === "in_progress") return "mails-progress-in_progress";
    if (progress === "completed") return "mails-progress-completed";
    return "mails-progress-pending";
  };

  const avatarPalette = ["#1e40af", "#0e7490", "#7c3aed", "#be185d", "#b45309", "#15803d", "#4338ca"];

  const getInitial = (name, email) => {
    const source = (name || email || "?").trim();
    return source.charAt(0).toUpperCase();
  };

  const getAvatarColor = (seed) => {
    const str = seed || "?";
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return avatarPalette[Math.abs(hash) % avatarPalette.length];
  };

  const resolveInlineImages = (html, mailUid) => {
    if (!html) return html;
    return html.replace(/cid:([^"')\s>]+)/gi, (match, cid) => {
      const decodedCid = decodeURIComponent(cid);
      return `${apiurl}mails.php?mode=inline_image&mail_uid=${mailUid}&cid=${encodeURIComponent(decodedCid)}`;
    });
  };

  const attachmentUrl = (id, download) =>
    `${apiurl}mails.php?mode=mail_attachment&id=${id}${download ? "&download=1" : ""}`;

  const invoiceFileUrl = (id, download) =>
    `${apiurl}mails.php?mode=invoice_file&id=${id}${download ? "&download=1" : ""}`;

  const replyAttachmentUrl = (id, download) =>
    `${apiurl}mails.php?mode=reply_attachment&id=${id}${download ? "&download=1" : ""}`;

  const previewFileUrl = (preview, download) => {
    if (preview.kind === "invoice") return invoiceFileUrl(preview.id, download);
    if (preview.kind === "reply_attachment") return replyAttachmentUrl(preview.id, download);
    return attachmentUrl(preview.id, download);
  };

  const handleDownload = (attachmentId, kind = "attachment") => {
    const url = kind === "reply_attachment" ? replyAttachmentUrl(attachmentId, true) : attachmentUrl(attachmentId, true);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openPreview = (kind, id, name) => {
    setPreviewMail({ kind, id, name });
  };

  const loadSheetPreview = useCallback(async (preview) => {
    const url = previewFileUrl(preview, false);
    try {
      setSheetLoading(true);
      setSheetError("");
      setSheetPreview(null);
      setSheetActiveTab(0);
      const response = await axios.get(url, { responseType: "arraybuffer" });
      const workbook = XLSX.read(response.data, { type: "array" });
      const sheets = workbook.SheetNames.map((name) => ({
        name,
        rows: XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, blankrows: false }),
      }));
      setSheetPreview(sheets);
    } catch (error) {
      setSheetError("Could not parse this file for preview. Use Download instead.");
    } finally {
      setSheetLoading(false);
    }
  }, []);

  const selfAssign = async (row) => {
    const confirm = await Swal.fire({
      title: "Assign to yourself?",
      text: "Once assigned, only an Accounts Head can reassign or unassign this mail.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, assign to me",
    });
    if (!confirm.isConfirmed) return;

    try {
      setAssigningId(row.id);
      const formData = new FormData();
      formData.append("mode", "self_assign");
      formData.append("mail_id", String(row.id));
      const response = await axios.post(`${apiurl}mails.php`, formData);
      if (response.data?.status === true) {
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  assigned_to: response.data.assigned_to,
                  assigned_name: response.data.assigned_name,
                  assigned_at: response.data.assigned_at,
                }
              : r
          )
        );
      } else {
        Swal.fire("Error", response.data?.message || "Someone else may have already grabbed this mail", "error");
        refreshSilently();
      }
    } catch (error) {
      Swal.fire("Error", "Could not update assignment", "error");
      refreshSilently();
    } finally {
      setAssigningId(null);
    }
  };

  const assignStaff = async (row, targetUsername) => {
    try {
      setAssigningId(row.id);
      const formData = new FormData();
      formData.append("mode", "assign_staff");
      formData.append("mail_id", String(row.id));
      formData.append("target_username", targetUsername);
      const response = await axios.post(`${apiurl}mails.php`, formData);
      if (response.data?.status === true) {
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  assigned_to: response.data.assigned_to,
                  assigned_name: response.data.assigned_name,
                  assigned_at: response.data.assigned_at,
                }
              : r
          )
        );
      } else {
        Swal.fire("Error", response.data?.message || "Could not reassign", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Could not reassign", "error");
    } finally {
      setAssigningId(null);
    }
  };

  const updateProgress = async (row, progress) => {
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, progress } : r)));
    try {
      const formData = new FormData();
      formData.append("mode", "update_progress");
      formData.append("mail_id", String(row.id));
      formData.append("progress", progress);
      const response = await axios.post(`${apiurl}mails.php`, formData);
      if (response.data?.status !== true) {
        Swal.fire("Error", response.data?.message || "Could not update progress", "error");
        refreshSilently();
      }
    } catch (error) {
      Swal.fire("Error", "Could not update progress", "error");
      refreshSilently();
    }
  };

  const openReplyWithMail = (mail) => {
    const subject = mail.subject || "";
    setReplyTarget(mail);
    setReplySubject(/^re:/i.test(subject.trim()) ? subject : `Re: ${subject}`);
    setReplyBody("");
    setReplyAttachments([]);
    setShowQuoted(true);
  };

  const openReplyForRow = async (row) => {
    try {
      setReplyLoadingId(row.id);
      const response = await axios.get(`${apiurl}mails.php`, { params: { mode: "detail", id: row.id } });
      if (response.data?.status === true) {
        openReplyWithMail(response.data.data);
      } else {
        Swal.fire("Error", "Could not load this mail", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Could not load this mail", "error");
    } finally {
      setReplyLoadingId(null);
    }
  };

  const closeReply = () => {
    setReplyTarget(null);
    setReplySubject("");
    setReplyBody("");
    setReplyAttachments([]);
  };

  const handleReplyAttachmentChange = (e) => {
    const files = Array.from(e.target.files || []);
    setReplyAttachments((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeReplyAttachment = (index) => {
    setReplyAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const sendReply = async () => {
    if (!replyTarget) return;
    if (!replyBody.trim() && replyAttachments.length === 0) {
      Swal.fire("Error", "Reply cannot be empty", "error");
      return;
    }
    try {
      setSendingReply(true);
      const formData = new FormData();
      formData.append("mode", "send_reply");
      formData.append("mail_id", String(replyTarget.id));
      formData.append("body", replyBody);
      replyAttachments.forEach((file) => formData.append("attachments[]", file));
      const response = await axios.post(`${apiurl}mails.php`, formData);
      if (response.data?.status === true) {
        Swal.fire("Sent", "Reply sent successfully", "success");
        if (detailMail && detailMail.id === replyTarget.id) {
          setDetailMail((prev) => ({
            ...prev,
            replies: [...(prev.replies || []), response.data.reply],
          }));
        }
        closeReply();
      } else {
        Swal.fire("Error", response.data?.message || "Could not send reply", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Could not send reply", "error");
    } finally {
      setSendingReply(false);
    }
  };

  const openMailDetail = (id) => {
    setDetailMailId(id);
  };

  const closeMailDetail = () => {
    setDetailMailId(null);
    setDetailMail(null);
  };

  useEffect(() => {
    if (!detailMailId) return;
    (async () => {
      try {
        setDetailLoading(true);
        setDetailMail(null);
        const response = await axios.get(`${apiurl}mails.php`, { params: { mode: "detail", id: detailMailId } });
        if (response.data?.status === true) {
          setDetailMail(response.data.data);
        }
      } catch (error) {
        Swal.fire("Error", "Could not load the mail", "error");
      } finally {
        setDetailLoading(false);
      }
    })();
  }, [detailMailId]);

  useEffect(() => {
    if (!previewMail) return;
    if (isSpreadsheetPreviewable(previewMail.name)) {
      loadSheetPreview(previewMail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMail]);

  const columnCount = 10;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const sentTotalPages = Math.max(1, Math.ceil(sentTotal / pageSize));

  return (
    <div className="main-content app-content mails-page">
      <div className="container-fluid mt-4">
        <div className="mails-tabs" role="tablist" aria-label="Mails sections">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`mails-tab ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
            >
              <i className={tab.icon} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {activeTab === "sent" ? (
          <>
            <div className="advance-payment-panel mb-3 mails-filters-panel">
              <div className="mails-filters-row">
                <form
                  className="d-flex mails-filter-search"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setSentPage(1);
                  }}
                >
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by subject or recipient"
                    value={sentSearch}
                    onChange={(e) => {
                      setSentSearch(e.target.value);
                      setSentPage(1);
                    }}
                    style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                  />
                  {sentSearch ? (
                    <button
                      type="button"
                      className="btn btn-light"
                      style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
                      onClick={() => {
                        setSentSearch("");
                        setSentPage(1);
                      }}
                      title="Clear search"
                    >
                      <i className="ri-close-line" />
                    </button>
                  ) : null}
                  <button
                    type="submit"
                    className="advance-payment-primary-btn"
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                  >
                    <i className="ri-search-line" /> Search
                  </button>
                </form>
              </div>
            </div>

            <div className="advance-payment-table table-responsive mails-table">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th style={{ width: "50px" }}>S.No</th>
                    <th>To</th>
                    <th>Subject</th>
                    <th style={{ width: "160px" }}>Sent By</th>
                    <th style={{ width: "150px" }}>Sent At</th>
                    <th className="text-center" style={{ width: "130px" }}>Attachment</th>
                    <th className="text-center" style={{ width: "90px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {sentLoading ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        Loading...
                      </td>
                    </tr>
                  ) : sentRows.length > 0 ? (
                    sentRows.map((row, index) => (
                      <tr key={row.id} className="mails-clickable-row" onClick={() => openMailDetail(row.mail_id)}>
                        <td>{index + 1}</td>
                        <td>{row.to_email}</td>
                        <td className="mails-subject-cell" title={row.subject}>
                          {row.subject}
                        </td>
                        <td>{row.sent_by_name || row.sent_by}</td>
                        <td>
                          <div>{formatDatePart(row.sent_at)}</div>
                          <div className="mails-received-time">{formatTimePart(row.sent_at)}</div>
                        </td>
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          {row.attachments && row.attachments.length > 0 ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                row.attachments.length === 1
                                  ? openPreview("reply_attachment", row.attachments[0].id, row.attachments[0].file_name)
                                  : setAttachmentsListRow({ attachments: row.attachments, isReply: true })
                              }
                            >
                              View{row.attachments.length > 1 ? ` (${row.attachments.length})` : ""}
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="btn btn-sm btn-light mails-view-btn"
                            title="Open original mail"
                            onClick={() => openMailDetail(row.mail_id)}
                          >
                            <VisibilityRoundedIcon style={{ fontSize: "18px" }} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="border-0">
                        <div className="mails-empty-state text-center py-5">
                          <img
                            src={process.env.PUBLIC_URL + "/images/media/no-data.svg"}
                            alt="No Data"
                            className="d-block mx-auto"
                          />
                          <p className="text-muted mb-0">No sent mails yet</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="text-muted">
                Page {sentPage} of {sentTotalPages} | Total: {sentTotal}
              </span>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setSentPage((p) => Math.max(1, p - 1))}
                  disabled={sentPage <= 1 || sentLoading}
                >
                  Prev
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setSentPage((p) => Math.min(sentTotalPages, p + 1))}
                  disabled={sentPage >= sentTotalPages || sentLoading}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="mails-type-toggle">
              {(sectionToggleOptionsByTab[activeTab] || sectionToggleOptionsByTab.payroll).map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`mails-type-toggle-btn ${sectionFilter === opt.value ? "active" : ""}`}
                onClick={() => {
                  setSectionFilter(opt.value);
                  setPage(1);
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button type="button" className="mails-sync-btn" disabled={syncing} onClick={manualSync}>
            <RefreshRoundedIcon className={syncing ? "mails-sync-spin" : ""} style={{ fontSize: "18px" }} />
            <span>{syncing ? "Fetching latest mails..." : "Fetch Latest Mails"}</span>
          </button>
        </div>

        <div className="advance-payment-panel mb-3 mails-filters-panel">
          <div className="mails-filters-row">
            <form
              className="d-flex mails-filter-search"
              onSubmit={(e) => {
                e.preventDefault();
                setPage(1);
              }}
            >
              <input
                type="text"
                className="form-control"
                placeholder="Search by subject or sender"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              />
              {search ? (
                <button
                  type="button"
                  className="btn btn-light"
                  style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                  title="Clear search"
                >
                  <i className="ri-close-line" />
                </button>
              ) : null}
              <button
                type="submit"
                className="advance-payment-primary-btn"
                style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              >
                <i className="ri-search-line" /> Search
              </button>
            </form>
            <Select
              className="mails-filter-select"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              options={progressFilterOptions}
              value={progressFilter}
              onChange={(opt) => {
                setProgressFilter(opt);
                setPage(1);
              }}
              isSearchable={false}
            />
            <Select
              className="mails-filter-select"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              options={assignedFilterOptions}
              value={assignedFilter}
              onChange={(opt) => {
                setAssignedFilter(opt);
                setPage(1);
              }}
              isSearchable={false}
            />
            <Select
              className="mails-filter-select"
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              options={dateRangeOptions}
              value={dateRangePreset}
              onChange={(opt) => {
                setDateRangePreset(opt);
                setPage(1);
              }}
              isSearchable={false}
            />
            {dateRangePreset?.value === "custom" && (
              <>
                <input
                  type="date"
                  className="form-control mails-filter-date"
                  value={customFrom}
                  onChange={(e) => {
                    setCustomFrom(e.target.value);
                    setPage(1);
                  }}
                />
                <input
                  type="date"
                  className="form-control mails-filter-date"
                  value={customTo}
                  onChange={(e) => {
                    setCustomTo(e.target.value);
                    setPage(1);
                  }}
                />
              </>
            )}
            <button type="button" className="btn btn-light" onClick={clearFilters}>
              Clear
            </button>
          </div>
        </div>

        <div className="advance-payment-table table-responsive mails-table">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th style={{ width: "50px" }}></th>
                <th style={{ width: "50px" }}>S.No</th>
                <th className="text-center" style={{ width: "200px" }}>Assigned</th>
                <th style={{ width: "200px" }}>Progress</th>
                <th>Subject</th>
                <th>From</th>
                <th style={{ width: "110px" }}>Type</th>
                <th style={{ width: "150px" }}>Received At</th>
                <th className="text-center" style={{ width: "130px" }}>Attachment</th>
                <th className="text-center" style={{ width: "100px" }}>Reply</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columnCount} className="text-center text-muted py-4">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    Loading...
                  </td>
                </tr>
              ) : rows.length > 0 ? (
                rows.map((row, index) => {
                  const isMine = row.assigned_to && row.assigned_to === currentUser;
                  const isUnassigned = !row.assigned_to;
                  return (
                    <tr
                      key={row.id}
                      className={`mails-clickable-row ${isUnassigned ? "mails-row-unassigned" : ""}`}
                      onClick={() => openMailDetail(row.id)}
                    >
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn btn-sm btn-light mails-view-btn"
                          title={isUnassigned ? "New — not yet assigned" : "View full mail"}
                          onClick={() => openMailDetail(row.id)}
                        >
                          <VisibilityRoundedIcon style={{ fontSize: "18px" }} />
                          {isUnassigned && <span className="mails-new-dot"></span>}
                        </button>
                      </td>
                      <td>{index + 1}</td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        {isAccountsHead ? (
                          <div>
                            <select
                              className="form-select form-select-sm"
                              value={row.assigned_to || ""}
                              disabled={assigningId === row.id}
                              onChange={(e) => assignStaff(row, e.target.value)}
                            >
                              <option value="">Unassigned</option>
                              {accountsStaff.map((s) => (
                                <option key={s.username} value={s.username}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                            {row.assigned_to && (
                              <div className="mails-assigned-at">{formatDateTime(row.assigned_at)}</div>
                            )}
                          </div>
                        ) : row.assigned_to ? (
                          <div>
                            <span
                              className={`badge ${isMine ? "bg-primary" : "bg-secondary"} px-3 py-2`}
                              title={isMine ? "Assigned to you" : `Assigned to ${row.assigned_name}`}
                            >
                              {row.assigned_name}
                            </span>
                            <div className="mails-assigned-at">{formatDateTime(row.assigned_at)}</div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            disabled={assigningId === row.id}
                            onClick={() => selfAssign(row)}
                          >
                            {assigningId === row.id ? "..." : "Assign to Me"}
                          </button>
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <select
                          className={`form-select form-select-sm ${progressColorClass(row.progress || "pending")}`}
                          value={row.progress || "pending"}
                          disabled={!isAccountsHead && !isMine}
                          title={!isAccountsHead && !isMine ? "Only the assignee or accounts head can update progress" : ""}
                          onChange={(e) => updateProgress(row, e.target.value)}
                        >
                          {progressOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="mails-subject-cell" title={row.subject}>{row.subject}</td>
                      <td>{row.from_name ? `${row.from_name} <${row.from_email}>` : row.from_email}</td>
                      <td>
                        <span className={`badge ${typeBadgeClass(row.type)} px-3 py-2`}>
                          {row.type === "payroll" ? "Payroll" : "Others"}
                        </span>
                      </td>
                      <td>
                        <div>{formatDatePart(row.received_at)}</div>
                        <div className="mails-received-time">{formatTimePart(row.received_at)}</div>
                      </td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        {row.attachments && row.attachments.length > 0 ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              row.attachments.length === 1
                                ? openPreview("attachment", row.attachments[0].id, row.attachments[0].file_name)
                                : setAttachmentsListRow(row)
                            }
                          >
                            View{row.attachments.length > 1 ? ` (${row.attachments.length})` : ""}
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn btn-sm mails-reply-btn"
                          disabled={replyLoadingId === row.id}
                          onClick={() => openReplyForRow(row)}
                        >
                          <ReplyRoundedIcon style={{ fontSize: "16px" }} />
                          <span>{replyLoadingId === row.id ? "..." : "Reply"}</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columnCount} className="border-0">
                    <div className="mails-empty-state text-center py-5">
                      <img
                        src={process.env.PUBLIC_URL + "/images/media/no-data.svg"}
                        alt="No Data"
                        className="d-block mx-auto"
                      />
                      <p className="text-muted mb-0">No mails found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted">
            Page {page} of {totalPages} | Total: {total}
          </span>
          <div className="btn-group">
            <button
              className="btn btn-outline-secondary"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              Prev
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>
          </>
        )}
      </div>

      {previewMail && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setPreviewMail(null)}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-truncate d-flex align-items-center gap-2">
                  {fileIcon(previewMail.name, "mails-attachment-icon")}
                  {previewMail.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setPreviewMail(null);
                    setSheetPreview(null);
                    setSheetError("");
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {(() => {
                  const fileName = previewMail.name;

                  if (isInlinePreviewable(fileName)) {
                    return (
                      <iframe
                        className="mails-preview-iframe"
                        src={previewFileUrl(previewMail, false)}
                        title="attachment-preview"
                      />
                    );
                  }

                  if (isSpreadsheetPreviewable(fileName)) {
                    if (sheetLoading) {
                      return (
                        <div className="text-center py-5 text-muted">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          Parsing spreadsheet...
                        </div>
                      );
                    }
                    if (sheetError) {
                      return <div className="text-muted text-center py-5">{sheetError}</div>;
                    }
                    if (sheetPreview && sheetPreview.length > 0) {
                      const activeSheet = sheetPreview[sheetActiveTab] || sheetPreview[0];
                      return (
                        <div>
                          {sheetPreview.length > 1 && (
                            <div className="d-flex gap-2 mb-2 flex-wrap">
                              {sheetPreview.map((sheet, idx) => (
                                <button
                                  key={sheet.name}
                                  type="button"
                                  className={`btn btn-sm ${idx === sheetActiveTab ? "btn-primary" : "btn-outline-secondary"}`}
                                  onClick={() => setSheetActiveTab(idx)}
                                >
                                  {sheet.name}
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="mails-sheet-preview">
                            <table className="table table-bordered table-sm mb-0">
                              <tbody>
                                {activeSheet.rows.map((row, rIdx) => (
                                  <tr key={rIdx}>
                                    {row.map((cell, cIdx) => (
                                      <td key={cIdx}>{cell !== undefined && cell !== null ? String(cell) : ""}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    }
                  }

                  return (
                    <div className="text-muted text-center py-5">
                      Preview isn't available for this file type. Use Download instead.
                    </div>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => window.open(previewFileUrl(previewMail, true), "_blank", "noopener,noreferrer")}
                >
                  Download
                </button>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    setPreviewMail(null);
                    setSheetPreview(null);
                    setSheetError("");
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {attachmentsListRow && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setAttachmentsListRow(null)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-truncate">Attachments</h5>
                <button type="button" className="btn-close" onClick={() => setAttachmentsListRow(null)}></button>
              </div>
              <div className="modal-body">
                <div className="mails-detail-attachments p-0 border-0">
                  {attachmentsListRow.attachments.map((att) => (
                    <AttachmentTile
                      key={att.id}
                      name={att.file_name}
                      onView={() => {
                        setAttachmentsListRow(null);
                        openPreview(attachmentsListRow.isReply ? "reply_attachment" : "attachment", att.id, att.file_name);
                      }}
                      onDownload={() => handleDownload(att.id, attachmentsListRow.isReply ? "reply_attachment" : "attachment")}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {detailMailId && (
        <div
          className="modal d-block mails-detail-modal-backdrop"
          tabIndex="-1"
          onClick={closeMailDetail}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content mails-detail-modal">
              {detailLoading ? (
                <div className="text-center py-5 text-muted">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Loading mail...
                </div>
              ) : detailMail ? (
                <>
                  <div className="mails-detail-topbar">
                    <h5 className="mails-detail-subject">{detailMail.subject || "(No subject)"}</h5>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm mails-reply-btn"
                        onClick={() => openReplyWithMail(detailMail)}
                      >
                        <ReplyRoundedIcon style={{ fontSize: "16px" }} />
                        <span>Reply</span>
                      </button>
                      <button type="button" className="btn-close" onClick={closeMailDetail}></button>
                    </div>
                  </div>

                  <div className="mails-detail-headers">
                    <div
                      className="mails-avatar"
                      style={{ backgroundColor: getAvatarColor(detailMail.from_email) }}
                    >
                      {getInitial(detailMail.from_name, detailMail.from_email)}
                    </div>
                    <div className="mails-detail-headers-main">
                      <div className="mails-detail-headers-top">
                        <div className="mails-detail-sender">
                          <span className="mails-detail-sender-name">{detailMail.from_name || detailMail.from_email}</span>
                          <span className="mails-detail-sender-email">&lt;{detailMail.from_email}&gt;</span>
                        </div>
                        <div className="mails-detail-date">{formatDateTime(detailMail.received_at)}</div>
                      </div>
                      {detailMail.to_email && (
                        <div className="mails-detail-to">to {detailMail.to_email}</div>
                      )}
                      <div className="mails-detail-badges">
                        <span className={`badge ${typeBadgeClass(detailMail.type)} px-3 py-1`}>
                          {detailMail.type === "payroll" ? "Payroll" : "Others"}
                        </span>
                        {detailMail.assigned_name && (
                          <span className="badge bg-primary-transparent text-primary px-3 py-1">
                            Assigned: {detailMail.assigned_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mails-detail-body">
                    {detailMail.body_html ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            resolveInlineImages(detailMail.body_html, detailMail.mail_uid),
                            { ADD_ATTR: ["target"] }
                          ),
                        }}
                      />
                    ) : detailMail.body_text ? (
                      <pre className="mails-detail-body-text">{detailMail.body_text}</pre>
                    ) : (
                      <p className="text-muted mb-0">No content found in this mail.</p>
                    )}
                  </div>

                  {((detailMail.attachments && detailMail.attachments.length > 0) || detailMail.invoice_file_name) && (
                    <div className="mails-detail-attachments">
                      {(detailMail.attachments || []).map((att) => (
                        <AttachmentTile
                          key={att.id}
                          name={att.file_name}
                          onView={() => {
                            closeMailDetail();
                            openPreview("attachment", att.id, att.file_name);
                          }}
                          onDownload={() => handleDownload(att.id)}
                        />
                      ))}
                      {detailMail.invoice_file_name && (
                        <AttachmentTile
                          name={detailMail.invoice_file_name}
                          label="(Invoice)"
                          onView={() => {
                            closeMailDetail();
                            openPreview("invoice", detailMail.id, detailMail.invoice_file_name);
                          }}
                        />
                      )}
                    </div>
                  )}

                  {detailMail.replies && detailMail.replies.length > 0 && (
                    <div className="mails-detail-replies">
                      {detailMail.replies.map((reply) => (
                        <div key={reply.id} className="mails-reply-card">
                          <div className="mails-reply-card-header">
                            <span className="mails-reply-card-sender">
                              {reply.sent_by_name || reply.sent_by}
                              <span className="mails-reply-card-to">to {reply.to_email}</span>
                            </span>
                            <span className="mails-reply-card-date">{formatDateTime(reply.sent_at)}</span>
                          </div>
                          <div
                            className="mails-reply-card-body"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reply.body_html) }}
                          />
                          {reply.attachments && reply.attachments.length > 0 && (
                            <div className="mails-reply-card-attachments">
                              {reply.attachments.map((att) => (
                                <AttachmentTile
                                  key={att.id}
                                  name={att.file_name}
                                  onView={() => {
                                    closeMailDetail();
                                    openPreview("reply_attachment", att.id, att.file_name);
                                  }}
                                  onDownload={() => handleDownload(att.id, "reply_attachment")}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-muted text-center py-5">Could not load this mail.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {replyTarget && (
        <div
          className="modal d-block mails-compose-modal"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeReply}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-truncate d-flex align-items-center gap-2">
                  <ReplyRoundedIcon style={{ fontSize: "18px" }} />
                  Reply
                </h5>
                <button type="button" className="btn-close" onClick={closeReply}></button>
              </div>
              <div className="modal-body">
                <div className="mails-compose-field-row">
                  <span className="mails-compose-field-label">To</span>
                  <span className="mails-compose-field-value">
                    {replyTarget.from_name ? `${replyTarget.from_name} <${replyTarget.from_email}>` : replyTarget.from_email}
                  </span>
                </div>
                <div className="mails-compose-field-row">
                  <span className="mails-compose-field-label">Subject</span>
                  <input
                    type="text"
                    className="form-control form-control-sm border-0 px-0 mails-compose-field-value"
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                  />
                </div>

                <textarea
                  className="mails-compose-textarea"
                  placeholder="Write your reply..."
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                />

                {replyAttachments.length > 0 && (
                  <div className="mails-compose-attachments">
                    {replyAttachments.map((file, idx) => (
                      <div key={`${file.name}-${idx}`} className="mails-compose-attachment-chip">
                        {fileIcon(file.name, "mails-attachment-icon")}
                        <span className="text-truncate" style={{ maxWidth: "160px" }}>
                          {file.name}
                        </span>
                        <button
                          type="button"
                          className="mails-compose-attachment-remove"
                          onClick={() => removeReplyAttachment(idx)}
                        >
                          <i className="ri-close-line" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="d-flex align-items-center gap-2 mt-2">
                  <input
                    type="file"
                    id="reply-attachment-input"
                    className="d-none"
                    multiple
                    onChange={handleReplyAttachmentChange}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => document.getElementById("reply-attachment-input")?.click()}
                  >
                    <AttachFileRoundedIcon style={{ fontSize: "16px" }} /> Attach
                  </button>
                </div>

                <button
                  type="button"
                  className="mails-compose-quoted-toggle"
                  onClick={() => setShowQuoted((s) => !s)}
                >
                  {showQuoted ? "Hide" : "Show"} quoted original mail
                </button>
                {showQuoted && (
                  <div className="mails-compose-quoted">
                    <div className="mails-compose-quoted-meta">
                      On {formatDateTime(replyTarget.received_at)},{" "}
                      {replyTarget.from_name
                        ? `${replyTarget.from_name} <${replyTarget.from_email}>`
                        : replyTarget.from_email}{" "}
                      wrote:
                    </div>
                    {replyTarget.body_html ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            resolveInlineImages(replyTarget.body_html, replyTarget.mail_uid),
                            { ADD_ATTR: ["target"] }
                          ),
                        }}
                      />
                    ) : (
                      <pre className="mails-detail-body-text">{replyTarget.body_text}</pre>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={closeReply}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" disabled={sendingReply} onClick={sendReply}>
                  {sendingReply ? "Sending..." : "Send Reply"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Mails;
